import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { cloneDeep, find } from "lodash";
import moment from "moment";
import { Subject } from "rxjs";
import {
  debounceTime,
  filter,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { FlowsService } from "src/app/flows/flows.service";
import { SessionService } from "src/app/session.service";
import { GeofenceSelectDialogComponent } from "src/app/shared/dialogs/geofence-select-dialog/geofence-select-dialog.component";
import { SignalPreviewDialogComponent } from "src/app/shared/dialogs/signal-preview-dialog/signal-preview-dialog.component";
import { SignalsService } from "src/app/signals/signals.service";
import { FlowMenuComponent } from "../../flow-menu/flow-menu.component";

interface Tab {
  code: string;
  name: string;
  touched: boolean;
  formGroup?: FormGroup | undefined;
  invalidProperty: string;
  feedbackControl?: FormControl;
  redemptionControl?: FormControl;
  icon?: string;
  disabled: boolean;
}

export interface FlowPayloadProperty {
  keyNamePrefix: string;
  keyControl: FormControl;
  valueControl: FormControl;
  completed: boolean;
  valueAutoCompleteOptions: any[];
  keyNameIsLookup: boolean;
}

export interface PayloadLookup {
  headers: {
    token: string;
  };
  label_path: string;
  method: HTTPMethod;
  name: string;
  url: string;
  value_path: string;
}

enum HTTPMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

@Component({
  selector: "app-phone-node-menu",
  templateUrl: "./phone-node-menu.component.html",
  styleUrls: ["./phone-node-menu.component.scss"],
})
export class PhoneNodeMenuComponent
  extends FlowMenuComponent
  implements OnInit
{
  tagsGroup!: FormGroup;
  templateOptions: any;
  trackingCategoryOptions: any;
  conversionTrackingOptions:
    | ({ label: string; value: null } | { label: string; value: string })[]
    | undefined = [];
  tags: any;
  locationOptions: any;
  selectedLocations: any = [];
  tagSearchValue: string = "";
  filteredTags: string[] = [];
  selectedTags: string[] = [];
  nonSelectedTags: string[] = [];
  tabs!: Tab[];
  currentTab!: Tab;
  hasPayloadForm: boolean = false;
  hasOffer: boolean = false;
  lookupKeys?: any[];
  payloadProperties?: FlowPayloadProperty[];
  payloadLookups?: PayloadLookup[];
  offers?: any[];
  offerControl?: FormControl;
  offerFields: any;
  intervalOptions!: string[];
  data!: { selected: boolean };
  frequencyOptions!: any[];

  contentAssistantIntegration: any;
  contentTabs!: Tab[];
  currentContentTab!: Tab;

  tempForm?: FormGroup;

  bodyContentModel: string = "";

  constructor(
    private SessionService: SessionService,
    private FlowsService: FlowsService,
    private Router: Router,
    private Dialog: MatDialog,
    private SignalsService: SignalsService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {

    this.contentAssistantIntegration = find(this.route.snapshot.data.integrations, (integration: any) => integration.provider.type === 'signal_content_assistant');

    this.frequencyOptions = this.FlowsService.getFrequencies('phone');

    this.hasPayloadForm =
      this.SessionService.currentAccountValue!.catalog?.signals?.payload?.display_form;

    this.hasOffer = !!this.SessionService.currentAppValue!.offers?.length;

    if (this.hasPayloadForm) {
      this.initPayload();
    }

    if (this.hasOffer) {
      this.initOffer();
    }

    this.templateOptions = this.FlowsService.templateOptions;

    this.trackingCategoryOptions = this.FlowsService.trackingCategoryOptions;

    this.conversionTrackingOptions =
      this.FlowsService.conversionTrackingOptions;

    this.locationOptions = this.FlowsService.locationOptions;

    this.tags = this.FlowsService.tags;

    this.nonSelectedTags = this.tags.filter(
      (tag: string) => !this.selectedTags.includes(tag),
    );

    let template,
      frequency,
      trackingCategory,
      pushNotificationHeadline,
      pushNotificationText,
      pushNotificationContent,
      conversionLength;

    if (this.node.data) {
      trackingCategory = this.node.data.account_signal_category_id;
      pushNotificationContent = this.node.data.body;
      frequency = this.node.data.frequency;
      pushNotificationText = this.node.data.preview;
      pushNotificationHeadline = this.node.data.title;
      template = this.templateOptions.find(
        (template: any) => template.id === this.node.data.signal_template_id,
      );
      conversionLength = this.node.data.flowData?.conversionLength;

      if (Array.isArray(this.node.data.tags)) {
        for (let tag of this.node.data.tags) {
          this.selectedTags.push(tag);
        }
      }
    }

    this.bodyContentModel = pushNotificationContent

    this.tabs = [
      {
        code: "content",
        name: "Content",
        touched: true,
        formGroup: new FormGroup({
          template: new FormControl(template, [Validators.required]),
          frequency: new FormControl(frequency, [Validators.required]),
          trackingCategory: new FormControl(trackingCategory, [
            Validators.required,
          ]),
          notificationHeadline: new FormControl(pushNotificationHeadline, [
            Validators.required,
          ]),
          notificationText: new FormControl(pushNotificationText, [
            Validators.required,
          ]),
          bodyContent: new FormControl(pushNotificationContent, [
            Validators.required,
          ]),
          redemption: new FormControl(
            this.node.data.has_redemption !== undefined ||
            this.node.data.has_redemption !== null
              ? this.node.data.has_redemption
              : template.has_redemption,
          ),
          feedback: new FormControl(
            this.node.data.has_feedback !== undefined ||
            this.node.data.has_feedback !== null
              ? this.node.data.has_feedback
              : template.has_feedback,
          ),
        }),
        invalidProperty: "contentInvalid",
        disabled: false,
      },
      {
        code: "tagsAndConversion",
        name: "Tags & Conversion",
        touched: false,
        formGroup: new FormGroup({
          locations: new FormControl(null),
          conversions: new FormControl(conversionLength),
        }),
        invalidProperty: "tagsAndConversionsInvalid",
        disabled: false,
      },
    ];

    this.tabs[0].formGroup!.valueChanges.subscribe((change) => {
      if(change.template !== null && this.contentTabs.length > 1){
        this.contentTabs[1].disabled = false;
      }
    });

    if (this.hasOffer || this.hasPayloadForm) {
      this.tabs.push({
        code: "payloadOrOffer",
        name: this.payloadOrOfferTabName,
        touched: false,
        invalidProperty: "payloadOrOfferInvalid",
        disabled: false,
      });
    }

    const contentTab = this.tabs?.find((tab) => tab.code === "content")!;

    contentTab.feedbackControl = contentTab.formGroup!.controls
      .feedback as FormControl;

    contentTab.redemptionControl = contentTab.formGroup!.controls
      .redemption as FormControl;

    this.currentTab = this.tabs[0];

    this.tagsGroup = this.tabs.find(
      (tab) => tab.code === "tagsAndConversion",
    )!.formGroup!;

    //TODO WHY AM I USING NAME HERE
    this.tagsGroup.controls.locations.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        this.selectedLocations = value.map((locationName: string) => {
          return this.locationOptions.find(
            (location: any) => location.name === locationName,
          );
        });
      });

    if (this.node.data.flowData) {
      let selectedLocations: any[] = [];

      if (
        this.node.data.flowData.conversionBeaconRegions !== undefined &&
        this.node.data.flowData.conversionBeaconRegions !== null
      )
        this.node.data.flowData.conversionBeaconRegions.forEach(
          (beaconRegionID: number) => {
            const beaconRegionLocationOption = this.locationOptions.find(
              (location: any) => {
                return (
                  location.type === "beacon-region" &&
                  location.id === beaconRegionID
                );
              },
            );

            if (beaconRegionLocationOption) {
              selectedLocations.push(beaconRegionLocationOption.name);
            }
          },
        );

      if (
        this.node.data.flowData.conversionGeofences !== undefined &&
        this.node.data.flowData.conversionGeofences !== null
      )
        this.node.data.flowData.conversionGeofences.forEach(
          (geofenceID: number) => {
            const geofenceLocationOption = this.locationOptions.find(
              (location: any) => {
                return (
                  location.type === "geofence" && location.id === geofenceID
                );
              },
            );

            selectedLocations.push(geofenceLocationOption.name);
          },
        );

      this.tagsGroup.controls.locations.setValue(selectedLocations);
    }

    this.contentTabs = [
      {
        code: "content",
        name: "Edit Content",
        touched: true,
        formGroup: contentTab.formGroup,
        invalidProperty: "contentInvalid",
        disabled: false,
      },
      {
        code: "assistant",
        name: "CorE",
        icon: "core-dark",
        touched: false,
        formGroup: contentTab.formGroup,
        invalidProperty: "assistantInvalid",
        disabled: template === undefined,
      },
    ]

    this.currentContentTab = this.contentTabs[0];

  }

  get signalFrequencies() {
    return this.frequencyOptions;
  }

  get contentInvalid() {
    const contentForm = this.tabs?.find((tab) => tab.code === "content")!
      .formGroup!;
    return contentForm?.invalid;
  }

  get tagsAndConversionsInvalid() {
    const tagsAndConversionsForm = this.tabs?.find(
      (tab) => tab.code === "tagsAndConversion",
    )!.formGroup!;

    return tagsAndConversionsForm?.invalid;
  }

  get formsInvalidity(): { [key: string]: boolean } {
    return {
      contentInvalid: this.contentInvalid,
      tagsAndConversionsInvalid: this.tagsAndConversionsInvalid,
      payloadOrOfferInvalid: this.payloadOrOfferInvalid,
    };
  }

  get payloadOrOfferInvalid() {
    if (this.hasOffer && this.offerFields) {
      const nonHiddenFields = this.offerFields.filter(
        (field: any) => !field.hidden,
      );
      for (let offerField of nonHiddenFields) {
        if (offerField.type === "Interval") {
          if (
            offerField.control.invalid ||
            offerField.intervalControl.invalid
          ) {
            return true;
          }
        } else if (offerField.type === "Date") {
          if (offerField.control.invalid || !offerField.timeString) {
            return true;
          }
        } else {
          if (offerField.control.invalid) {
            return true;
          }
        }
      }
    }
    if (this.hasPayloadForm) {
    }
    return false;
  }

  get invalid() {
    return (
      this.contentInvalid ||
      this.tagsAndConversionsInvalid ||
      this.payloadOrOfferInvalid
    );
  }

  get contentGroup() {
    return this.tabs?.find((tab) => tab.code === "content")!.formGroup!;
  }

  get payloadOrOfferTabName() {
    if (this.hasPayloadForm && this.hasOffer) {
      return "Payload & Offer";
    } else if (this.hasPayloadForm) {
      return "Payload";
    } else if (this.hasOffer) {
      return "Offer";
    }

    return "";
  }

  get payloadDTO() {
    const obj: any = {};

    if (this.hasPayloadForm) {
      const completedProperties = this.payloadProperties!.filter(
        (property) => property.completed,
      );

      for (let property of completedProperties) {
        obj[property.keyControl.value] = property.valueControl.value;
      }
    }

    return obj;
  }

  get offerDataDTO() {
    const obj: any = {};
    if (this.hasOffer && this.offerControl?.value?.id) {
      const nonHiddenFields = this.offerFields.filter(
        (field: any) => !field.hidden,
      );
      for (let offerField of nonHiddenFields) {
        if (offerField.type === "Interval") {
          obj[offerField.id] = {
            period: offerField.intervalControl.value,
            count: offerField.control.value,
          };
        } else if (offerField.type === "Date") {
          const dateTime = new Date(offerField.control.value);

          const [hour, minutes] = moment(offerField.timeString, "hh:mm A")
            .format("HH:mm")
            .split(":");

          dateTime.setHours(Number.parseInt(hour));
          dateTime.setMinutes(Number.parseInt(minutes));
          obj[offerField.id] = dateTime.toISOString();
        } else if (offerField.type === "Integer") {
          obj[offerField.id] = Number.parseInt(offerField.control.value);
        } else {
          obj[offerField.id] = offerField.control.value;
        }
      }
    }

    return obj;
  }

  get accountAppOfferID() {
    if (this.hasOffer && this.offerControl?.value?.id) {
      return this.offerControl?.value?.id;
    }

    return null;
  }

  initPayload() {
    this.payloadLookups =
      this.SessionService.currentAccountValue!.catalog.signals.payload.lookups;

    this.lookupKeys = this.getLookupKeyNames();

    this.payloadProperties = [];

    if (this.node.data.flowData?.payload) {
      for (let payloadKey of Object.keys(this.node.data.flowData.payload)) {
        this.payloadProperties.push({
          keyControl: new FormControl({ value: payloadKey, disabled: true }, [
            Validators.required,
          ]),
          valueControl: new FormControl(
            {
              value: this.node.data.flowData.payload[payloadKey],
              disabled: true,
            },
            [Validators.required],
          ),
          completed: true,
          keyNameIsLookup: false,
          keyNamePrefix: "Enter",
          valueAutoCompleteOptions: [],
        });
      }
    }

    this.pushNewPayloadProperty();
  }

  initOffer() {
    this.intervalOptions = ["hours", "days", "months", "years"];

    this.offers = [
      { name: "Do Not Send Offer", id: null },
      ...this.SessionService.currentAppValue!.offers,
    ];
    this.offerControl = new FormControl(null);

    this.offerControl.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (!!change.id) {
          this.offerFields = change.config.fields.map((field: any) => {
            const offerField = {
              ...field,
              control: new FormControl(),
            };
            if (field.required) {
              offerField.control.setValidators([Validators.required]);
            }

            if (field.type === "Interval") {
              offerField.intervalControl = new FormControl(null, [
                Validators.required,
              ]);
            }

            if (field.type === "Date") {
              offerField.timeString = null;
            }

            return offerField;
          });
          for (let offerField of this.offerFields) {
            if (offerField.type === "Select") {
              offerField.control.valueChanges
                .pipe(takeUntil(this.onComponentDestroy))
                .subscribe((change: any) => {
                  if (change.hides) {
                    for (let field of change.hides) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.hidden = true;
                      }
                    }
                  }

                  if (change.shows) {
                    for (let field of change.shows) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.hidden = false;
                      }
                    }
                  }

                  if (change.requires) {
                    for (let field of change.requires) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.required = true;
                        offerField.control.setValidators([Validators.required]);
                      }
                    }
                  }

                  if (change.notrequires) {
                    for (let field of change.notrequires) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.required = false;
                        offerField.control.clearValidators();
                      }
                    }
                  }
                });
            }

            if (
              this.node.data.flowData.accountAppOfferData &&
              this.node.data.flowData.accountAppOfferData[offerField.id]
            ) {
              if (offerField.type === "Select") {
                const option = offerField.options.find(
                  (option: any) =>
                    option.value ===
                    this.node.data.flowData.accountAppOfferData[offerField.id]
                      .value,
                );
                if (option) {
                  offerField.control.setValue(option);
                }
              } else if (offerField.type === "Interval") {
                offerField.control.setValue(
                  this.node.data.flowData.accountAppOfferData[offerField.id]
                    .count,
                );
                offerField.intervalControl.setValue(
                  this.node.data.flowData.accountAppOfferData[offerField.id]
                    .period,
                );
              } else if (offerField.type === "Date") {
                offerField.control.setValue(
                  this.node.data.flowData.accountAppOfferData[offerField.id],
                );

                if (
                  this.node.data.flowData.accountAppOfferData[offerField.id]
                ) {
                  offerField.timeString = moment(
                    this.node.data.flowData.accountAppOfferData[offerField.id],
                  ).format("hh:mm A");
                }
              } else {
                offerField.control.setValue(
                  this.node.data.flowData.accountAppOfferData[offerField.id],
                );
              }
            }
          }
        } else {
          this.offerFields = [];
        }
      });

    if (this.node.data.flowData && this.node.data.flowData.accountAppOfferID) {
      const offer = this.offers.find(
        (offer) => offer.id === this.node.data.flowData.accountAppOfferID,
      );
      this.offerControl.setValue(offer);
    }
  }

  createNewTemplate() {
    //TODO ARE YOU SURE YOU WANT TO LEAVE PAGE IF ANY CHANGES MADE
    this.Router.navigate(["content", "template", "create"]);
  }

  selectLocations() {
    const dialogRef = this.dialog.open(GeofenceSelectDialogComponent, {
      width: "500px",
      data: {
        geofences: this.locationOptions,
        selected: this.selectedLocations,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedLocations = result;
      }
    });
  }

  locationRemoved(removedLocation: any) {
    const locations = this.tagsGroup.controls.locations.value.filter(
      //TODO WHY AM I USING NAME HERE
      (location: any) => location !== removedLocation.name,
    );
    this.tagsGroup.controls.locations.setValue(locations);
  }

  applyTagFilter() {
    if (!this.tagSearchValue || this.tagSearchValue === "") {
      this.filteredTags = [];
    } else {
      this.filteredTags = this.nonSelectedTags.filter((tag) =>
        tag.toLowerCase().includes(this.tagSearchValue.toLowerCase()),
      );
    }
  }

  tagSelected(tag: any) {
    this.selectedTags.push(tag);
    this.nonSelectedTags = this.nonSelectedTags.filter(
      (nonSelectedTag) => nonSelectedTag !== tag,
    );
    this.applyTagFilter();
  }

  tagRemoved(tag: any) {
    this.selectedTags = this.selectedTags.filter(
      (selectTag) => selectTag !== tag,
    );
    this.nonSelectedTags.push(tag);
    this.applyTagFilter();
  }

  createTag() {
    const newTag = this.tagSearchValue;
    this.tagSearchValue = "";
    this.tags.push(newTag);
    this.selectedTags.push(newTag);
  }

  contentTabClicked(tab: any) {
    if(!tab.disabled) {
      this.currentContentTab = tab; 
      this.currentContentTab.touched = true;
    } else {
      this.currentTab.formGroup!.markAllAsTouched();
    }
  }

  endAssistant() {
    this.currentContentTab = this.contentTabs[0];
  }

  preview(type: string, args?: any) {
    //console.log('phone node preview', type, args);
    this.getSignalPreview(args)
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        const resTemplate = (res as unknown as any).template;
        let data = {
          type,
          preview: resTemplate,
          formGroup: this.contentGroup,
          templateOptions: this.templateOptions,
          templateControl: this.contentGroup.controls.template,
          headlineControl: this.contentGroup.controls.notificationHeadline,
          notificationControl: this.contentGroup.controls.notificationText,
          contentControl: this.contentGroup.controls.bodyContent,
          feedbackControl: this.contentGroup.controls.feedback,
          redemptionControl: this.contentGroup.controls.redemption,
          forAssistant: false,
        };
        if(args !== undefined){
          this.tempForm = new FormGroup({
            headlineControl: new FormControl(args.data.data.arguments.headline),
            notificationControl: new FormControl(args.data.data.arguments.push_notification),
            contentControl: new FormControl(args.data.data.arguments.body),
          });
          data.headlineControl = this.tempForm.controls.headlineControl;
          data.notificationControl = this.tempForm.controls.notificationControl;
          data.contentControl = this.tempForm.controls.contentControl;
          data.forAssistant = true;
        }
        const dialogRef = this.dialog.open(SignalPreviewDialogComponent, {
          data: data,
          disableClose: args !== undefined,
        });
        dialogRef.afterClosed().subscribe((result) => {
          //console.log('phone node preview result', result);
          if(args === undefined){
            return;
          }
          if(result === false){
            args.resultFn({accepted: false});
          } else if (result === true) {
            console.log(this.tempForm!.value);
            this.contentGroup.controls.notificationHeadline.setValue(this.tempForm!.controls.headlineControl.value);
            this.contentGroup.controls.notificationText.setValue(this.tempForm!.controls.notificationControl.value);
            this.contentGroup.controls.bodyContent.setValue(this.tempForm!.controls.contentControl.value);
            this.bodyContentModel = this.tempForm!.controls.contentControl.value;
            args.resultFn({accepted: true});
          }
        });
      });
  }

  getSignalPreview(args?: any) {
    let previewDTO = this.previewDTO;
    if(args !== undefined){
      previewDTO.headline = args.data.data.arguments.headline;
      previewDTO.notification = args.data.data.arguments.push_notification;
      previewDTO.content = args.data.data.arguments.body;
    }
    return this.SignalsService.getSignalPreview(
      this.SessionService.currentAccountValue!.accountID,
      previewDTO,
    );
  }

  addPayloadProperty(currentProperty: FlowPayloadProperty) {
    currentProperty.completed = true;
    currentProperty.keyControl.disable();
    currentProperty.valueControl.disable();
    this.pushNewPayloadProperty();
  }

  removePayloadProperty(currentProperty: FlowPayloadProperty) {
    this.payloadProperties = this.payloadProperties!.filter(
      (property) => property !== currentProperty,
    );
  }

  pushNewPayloadProperty() {
    const payLoadProperty: FlowPayloadProperty = {
      keyControl: new FormControl("", [Validators.required]),
      valueControl: new FormControl("", [Validators.required]),
      completed: false,
      keyNameIsLookup: false,
      keyNamePrefix: "Enter",
      valueAutoCompleteOptions: [],
    };
    payLoadProperty.keyControl.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (this.lookupKeys?.includes(value)) {
          payLoadProperty.keyNameIsLookup = true;
          payLoadProperty.keyNamePrefix = "Search";
        } else {
          if (payLoadProperty.keyNameIsLookup) {
            payLoadProperty.keyNameIsLookup = false;
            payLoadProperty.keyNamePrefix = "Enter";
          }
        }
      });

    payLoadProperty.valueControl.valueChanges
      .pipe(
        startWith([]),
        filter(
          (value) =>
            payLoadProperty.keyNameIsLookup && value && value.length > 2,
        ),
        debounceTime(300),
        switchMap((value) =>
          this.FlowsService.getPayloadLookup(
            this.SessionService.currentAccountValue!.accountID,
            payLoadProperty.keyControl.value,
            value,
          ),
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res: any) => {
        payLoadProperty.valueAutoCompleteOptions = res;
      });

    this.payloadProperties!.push(payLoadProperty);
  }

  bodyContentChanged(event: any) {
    this.currentTab.formGroup!.controls.bodyContent.setValue(event);
  }

  getLookupKeyNames() {
    return (
      this.payloadLookups?.map(
        (payloadLookup) => (payloadLookup as any)[payloadLookup.label_path],
      ) ?? []
    );
  }

  onTimePickerTimeChanged(time: string, field: any) {
    field.timeString = time;
  }

  get nodeDTO() {
    const contentForm = this.tabs.find((tab) => {
      return tab.code === "content";
    })?.formGroup!;

    return {
      id: this.node.id,
      type: "signal",
      top: this.node.top,
      left: this.node.left,
      is_follow_up: this.node.is_follow_up,
      data: {
        account_signal_category_id: contentForm.controls.trackingCategory.value,
        body: contentForm.controls.bodyContent.value,
        delay: 0,
        frequency: contentForm.controls.frequency.value,
        preview: contentForm.controls.notificationText.value,
        signal_template_id: contentForm.controls.template.value.id,
        title: contentForm.controls.notificationHeadline.value,
        type: "push",
        tags: this.selectedTags,
        has_feedback: contentForm.controls.feedback.value,
        has_redemption: contentForm.controls.redemption.value,
      },
    };
  }

  get previewDTO() {
    return {
      ...this.contentGroup.controls.template.value,
      has_feedback: this.contentGroup.controls.feedback.value,
      has_redemption: this.contentGroup.controls.redemption.value,
      headline: this.contentGroup.controls.notificationHeadline.value,
      notification: this.contentGroup.controls.notificationText.value,
      content: this.contentGroup.controls.bodyContent.value,
    };
  }

  get flowDTO() {
    const conversionGeofences = this.selectedLocations
      .filter((location: any) => location.type === "geofence")
      .map((location: any) => location.id);

    const conversionBeaconRegions = this.selectedLocations
      .filter((location: any) => location.type === "beacon-region")
      .map((location: any) => location.id);

    const conversionLocations = this.selectedLocations.map((location: any) => {
      return {
        type: location.type,
        id: location.id,
      };
    });

    const tagsAndConversionForm = this.tabs.find((tab) => {
      return tab.code === "tagsAndConversion";
    })?.formGroup!;

    const conversionLength = tagsAndConversionForm.controls.conversions.value;

    const payload = this.payloadDTO;

    const offerID = this.accountAppOfferID;

    const offerData = this.offerDataDTO;

    return {
      conversion_beacon_regions: conversionBeaconRegions,
      conversion_geofences: conversionGeofences,
      conversionLocations,
      payload,
      account_app_offer_id: offerID,
      account_app_offer_data: offerData,
      conversionLength: conversionLength,
    };
  }

  get contentAssistantData() {
    return {
      headline: this.tabs[0].formGroup!.controls['notificationHeadline'].value,
      notification: this.tabs[0].formGroup!.controls['notificationText'].value,
      body: this.tabs[0].formGroup!.controls['bodyContent'].value,
    }
  }
}
